import React from "react";
import { PICO_COLORS, highlightPICO } from "../../../../utils/Picocolors";

const isValidField = (val) => val && val !== "nan" && String(val).trim() !== "";

// Derived by the import engine from the source's own publication types, so a
// PubMed "Conference Proceedings" and an Embase "Conference Abstract" read the
// same here. OTHERS is deliberately shown rather than hidden -- it is how an
// unrecognised type stays visible instead of being silently bucketed.
const CATEGORY_LABELS = {
    JOURNAL: "Journal",
    CONFERENCE: "Conference",
    OTHERS: "Other",
};

const ScreeningAbstractPanel = ({ literature }) => {
    if (!literature) return null;

    const hasCitationFields = isValidField(literature.publication_type)
        || isValidField(literature.journal_abbr)
        || isValidField(literature.volume)
        || isValidField(literature.issue)
        || isValidField(literature.pages)
        || isValidField(literature.doi)
        || isValidField(literature.publication_category)
        || isValidField(literature.embase_id)
        || (literature.language || []).length > 0;

    // Name, location and date arrive together or not at all, so one missing
    // key means the source simply did not supply it.
    const conference = literature.conference_details || null;
    const conferenceLine = conference
        ? [conference.name, conference.location, conference.date].filter(Boolean).join(" · ")
        : "";

    return (
        <div className="ev-card-2 p-3 vh-75 d-flex flex-column text-justify pb-2">

            {/* Header: Title & Author */}
            <div className="mb-3">
                <p className="ev-bold-reg ev-med-p-blue mb-1">
                    {literature.title}
                </p>

                {literature.author_names && (
                    <p className="ev-xsmall-p-dark mb-0">
                        Authors. {literature.author_names}
                    </p>
                )}

                {hasCitationFields && (
                    <div className="d-flex align-items-center flex-wrap gap-2 mt-2">
                        {isValidField(literature.publication_type) && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                {literature.publication_type}
                            </span>
                        )}
                        {isValidField(literature.journal_abbr) && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-bold-reg ev-fs-11">
                                Journal: {literature.journal_abbr}
                            </span>
                        )}
                        {isValidField(literature.volume) && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                Vol: {literature.volume}
                            </span>
                        )}
                        {isValidField(literature.issue) && (
                            <span className="ev-message-card5 px-2  py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                Issue: {literature.issue}
                            </span>
                        )}
                        {isValidField(literature.pages) && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                Pages: {literature.pages}
                            </span>
                        )}
                        {!isValidField(literature.publication_type)
                            && isValidField(literature.publication_category) && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                {CATEGORY_LABELS[literature.publication_category] || literature.publication_category}
                            </span>
                        )}
                        {(literature.language || []).length > 0 && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                Lang: {literature.language.join(", ")}
                            </span>
                        )}
                        {isValidField(literature.embase_id) && (
                            <span className="ev-message-card5 px-2 py-1 ev-radius-xsmall ev-bold-med ev-fs-11">
                                Embase: {literature.embase_id}
                            </span>
                        )}
                        {isValidField(literature.doi) && (
                            <button
                                className="ev-btn-lightblue-fill-round px-2 py-1 ev-radius-xsmall ev-bold-med avatar-tooltip"
                                data-tooltip="Open DOI page"
                                onClick={() => window.open(`https://doi.org/${literature.doi}`, "_blank", "noopener,noreferrer")}
                            >
                                DOI <i className="bi bi-box-arrow-up-right ev-fs-10 ms-1" />
                            </button>
                        )}
                        {isValidField(literature.full_text_link) && (
                            <button
                                className="ev-btn-lightblue-fill-round px-2 py-1 ev-radius-xsmall ev-bold-med avatar-tooltip"
                                data-tooltip="Open publisher full text"
                                onClick={() => window.open(literature.full_text_link, "_blank", "noopener,noreferrer")}
                            >
                                Full text <i className="bi bi-box-arrow-up-right ev-fs-10 ms-1" />
                            </button>
                        )}
                    </div>
                )}

                {conferenceLine && (
                    <p className="ev-xsmall-p-dark mb-0 mt-2">
                        <i className="bi bi-people me-1" />
                        {conferenceLine}
                    </p>
                )}
            </div>

            {/* ===== Scrollable Content ===== */}
            <div className="flex-grow-1 scrollable-element pe-2">
                <p
                    className="ev-small-p-dark"
                    style={{ lineHeight: "1.6" }} // Added line height for readability
                    dangerouslySetInnerHTML={{
                        __html: highlightPICO(
                            (!literature.abstract || literature.abstract === "nan") ? "Not Available" : literature.abstract,
                            literature.pico_json || {}
                        ),
                    }}
                />
            </div>

            {/* ===== Legend ===== */}
            <div className="ev-card-8 mt-2 p-2 d-flex flex-wrap justify-content-center gap-3 ev-radius-small px-1">
                {Object.entries(PICO_COLORS).map(([key, color]) => (
                    <div key={key} className="d-flex align-items-center gap-2">
                        <span style={{
                            width: 12,
                            height: 12,
                            backgroundColor: color,
                            borderRadius: 3
                        }} />
                        <span className="ev-xsmall-p-dark ev-bold-reg">
                            {key === "comparison"
                                ? "Comparator"
                                : key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())}
                        </span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ScreeningAbstractPanel;